import {
  RUNTIME_TO_TOOL_ID,
  getToolById,
  isShapeRuntimeTool,
} from '@canvas/shared-types';
import { useCanvasStore } from '@/stores/canvasStore';
import { useLayoutMode } from '@/hooks/useLayoutMode';
import { isLinearTool } from '@/lib/shapeFactory';

interface OptionsBarProps {
  cropActive?: boolean;
  onCropApply?: () => void;
  onCropCancel?: () => void;
}

export default function OptionsBar({
  cropActive = false,
  onCropApply,
  onCropCancel,
}: OptionsBarProps) {
  const layoutMode = useLayoutMode();
  const isPhone = layoutMode === 'phone';
  const {
    selectedTool,
    selectedColor,
    gradientToColor,
    brushSize,
    opacity,
    polygonSides,
    marqueeShape,
    additiveSelect,
    setSelectedColor,
    setGradientToColor,
    setBrushSize,
    setOpacity,
    setPolygonSides,
    setMarqueeShape,
    setAdditiveSelect,
  } = useCanvasStore();

  const def = getToolById(RUNTIME_TO_TOOL_ID[selectedTool]);
  const toolName = def ? `${def.label} Tool` : selectedTool;
  const isShape = isShapeRuntimeTool(selectedTool);
  const isLinear = isShape && isLinearTool(selectedTool);

  return (
    <div className="ps-optionsbar" role="toolbar" aria-label="Tool options">
      <span className="tool-name">{toolName}</span>

      {selectedTool === 'select' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          {isPhone
            ? 'Tap to select · Pinch zoom · 2-finger pan · Double-tap fit/100%'
            : 'Space-pan · M marquee · I eyedropper · C crop · Ctrl+G group'}
        </span>
      )}

      {selectedTool === 'hand' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          {isPhone
            ? 'Drag with one finger to pan · Pinch to zoom'
            : 'Drag to pan the pasteboard · or hold Space'}
        </span>
      )}

      {selectedTool === 'marquee' && (
        <>
          <label>
            Shape
            <select
              value={marqueeShape}
              onChange={(e) =>
                setMarqueeShape(e.target.value === 'ellipse' ? 'ellipse' : 'rect')
              }
            >
              <option value="rect">Rectangle</option>
              <option value="ellipse">Ellipse</option>
            </select>
          </label>
          <label className="ps-options-toggle">
            <input
              type="checkbox"
              checked={additiveSelect}
              onChange={(e) => setAdditiveSelect(e.target.checked)}
            />
            Add to selection
          </label>
          <span style={{ color: 'var(--ps-muted)' }}>
            {isPhone
              ? 'Drag to select · use Add to selection instead of Shift'
              : 'Drag to multi-select · Shift add · M cycles shape'}
          </span>
        </>
      )}

      {selectedTool === 'lasso' && (
        <>
          <label className="ps-options-toggle">
            <input
              type="checkbox"
              checked={additiveSelect}
              onChange={(e) => setAdditiveSelect(e.target.checked)}
            />
            Add to selection
          </label>
          <span style={{ color: 'var(--ps-muted)' }}>
            {isPhone
              ? 'Loop on a photo to cut out that region · or loop shapes to select'
              : 'On a photo: cut out the loop (rest becomes transparent) · on shapes: select by center · L'}
          </span>
        </>
      )}

      {selectedTool !== 'select' &&
        selectedTool !== 'hand' &&
        selectedTool !== 'zoom' &&
        selectedTool !== 'eraser' &&
        selectedTool !== 'eyedropper' &&
        selectedTool !== 'crop' &&
        selectedTool !== 'marquee' &&
        selectedTool !== 'lasso' &&
        selectedTool !== 'bucket' && (
          <>
            <label>
              {selectedTool === 'gradient'
                ? 'From'
                : isLinear
                  ? 'Stroke'
                  : 'Fill'}
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
              />
            </label>

            {selectedTool === 'gradient' && (
              <label>
                To
                <input
                  type="color"
                  value={gradientToColor}
                  onChange={(e) => setGradientToColor(e.target.value)}
                />
              </label>
            )}

            {selectedTool !== 'gradient' && (
              <label>
                Opacity
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                />
                <span>{Math.round(opacity * 100)}%</span>
              </label>
            )}
          </>
        )}

      {(selectedTool === 'draw' ||
        selectedTool === 'pencil' ||
        isLinear ||
        (isShape && !isLinear)) && (
        <label>
          {isShape && !isLinear ? 'Stroke' : 'Size'}
          <input
            type="range"
            min={isShape && !isLinear ? 0 : 1}
            max={100}
            value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
          />
          <span>{brushSize}px</span>
        </label>
      )}

      {selectedTool === 'pencil' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Hard pencil strokes · N key
        </span>
      )}

      {selectedTool === 'draw' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Soft freehand brush · B key
        </span>
      )}

      {selectedTool === 'polygon' && (
        <label>
          Sides
          <input
            type="number"
            min={3}
            max={12}
            value={polygonSides}
            onChange={(e) => setPolygonSides(Number(e.target.value))}
          />
        </label>
      )}

      {isShape && !isLinear && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Click and drag · Press U to cycle shapes
        </span>
      )}

      {isLinear && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Click and drag to draw · U cycles shapes
        </span>
      )}

      {selectedTool === 'text' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Click or drag to place heading text (same as Libraries → Elements → Text) · T cycles
        </span>
      )}

      {selectedTool === 'textbox' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Click or drag to place body text box · resize handles update font size
        </span>
      )}

      {selectedTool === 'frame' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Drag a frame · select it · Upload image to fill
        </span>
      )}

      {selectedTool === 'gradient' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Click a shape or image to apply gradient (From → To)
        </span>
      )}

      {selectedTool === 'bucket' && (
        <>
          <label>
            Fill
            <input
              type="color"
              value={selectedColor}
              onChange={(e) => setSelectedColor(e.target.value)}
            />
          </label>
          <label>
            Opacity
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={opacity}
              onChange={(e) => setOpacity(Number(e.target.value))}
            />
            <span>{Math.round(opacity * 100)}%</span>
          </label>
          <span style={{ color: 'var(--ps-muted)' }}>
            Click a shape to fill · empty artboard for background · K
          </span>
        </>
      )}

      {selectedTool === 'eyedropper' && (
        <>
          <label>
            Sample
            <input type="color" value={selectedColor} readOnly title="Sampled color" />
          </label>
          <span style={{ color: 'var(--ps-muted)' }}>
            Click any object to sample its fill or stroke
          </span>
        </>
      )}

      {selectedTool === 'crop' && (
        <>
          <button
            type="button"
            className="ps-opt-btn"
            disabled={!cropActive}
            onClick={onCropApply}
          >
            Apply
          </button>
          <button
            type="button"
            className="ps-opt-btn"
            disabled={!cropActive}
            onClick={onCropCancel}
          >
            Cancel
          </button>
          <span style={{ color: 'var(--ps-muted)' }}>
            {cropActive
              ? 'Adjust the frame · Enter apply · Esc cancel'
              : 'Select an image, or drag on empty artboard to crop canvas'}
          </span>
        </>
      )}

      {selectedTool === 'pen' && (
        <>
          <label>
            Size
            <input
              type="range"
              min={1}
              max={40}
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
            />
            <span>{brushSize}px</span>
          </label>
          <span style={{ color: 'var(--ps-muted)' }}>
            Click to add points · Enter or double-click to finish · Esc cancel
          </span>
        </>
      )}

      {selectedTool === 'comment' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Click the artboard to drop a comment pin
        </span>
      )}

      {selectedTool === 'hand' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Drag to pan · Hold Space from any tool for temporary pan
        </span>
      )}

      {selectedTool === 'eraser' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Click or drag over objects to remove them
        </span>
      )}

      {selectedTool === 'zoom' && (
        <span style={{ color: 'var(--ps-muted)' }}>
          Click to zoom in · Alt+click to zoom out · Ctrl+wheel · Ctrl+0 = 100%
        </span>
      )}
    </div>
  );
}
